import clsx from 'clsx';
import NumberInput from 'components/inputs/number';
import { useEffect, useRef, useState } from 'react';
import {
  formatPresetLabel,
  isCustomPresetValue,
  toNumericValue
} from 'utils/number-preset-input';

export type NumberPresetProps = {
  presets: number[];
  onChange?: (value: number | undefined) => void;
  value?: any;
  suffix?: string;
  min?: number;
  max?: number;
  debounce?: number;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
};

export default function NumberPresetInput({
  presets,
  onChange,
  value,
  suffix,
  min,
  max,
  debounce,
  disabled,
  invalid,
  placeholder
}: NumberPresetProps) {
  const numericValue = toNumericValue(value);
  const hasCustomValue = isCustomPresetValue(numericValue, presets);
  const [otherSelected, setOtherSelected] = useState(hasCustomValue);
  const [otherAutoFocus, setOtherAutoFocus] = useState(false);
  const emittedValueRef = useRef(numericValue);

  useEffect(() => {
    const isExternalChange = numericValue !== emittedValueRef.current;
    emittedValueRef.current = numericValue;
    if (hasCustomValue) {
      setOtherSelected(true);
    } else if (isExternalChange) {
      setOtherSelected(false);
      setOtherAutoFocus(false);
    }
  }, [numericValue, hasCustomValue]);

  const emitChange = (newValue: number | undefined) => {
    emittedValueRef.current = newValue;
    if (onChange) {
      onChange(newValue);
    }
  };

  const handlePresetClick = (preset: number) => {
    const deselecting = !otherSelected && numericValue === preset;
    setOtherSelected(false);
    setOtherAutoFocus(false);
    emitChange(deselecting ? undefined : preset);
  };

  const handleOtherClick = () => {
    setOtherSelected(true);
    setOtherAutoFocus(true);
  };

  const chips = [
    ...presets.map((preset) => ({
      key: preset.toString(),
      label: formatPresetLabel(preset, suffix),
      selected: !otherSelected && numericValue === preset,
      onClick: () => handlePresetClick(preset)
    })),
    {
      key: 'other',
      label: 'Other',
      selected: otherSelected,
      onClick: handleOtherClick
    }
  ];

  return (
    <div className='flex items-center gap-2'>
      <span
        className={clsx(
          'isolate inline-flex rounded-md shadow-sm',
          invalid && 'ring-2 ring-red-500'
        )}
      >
        {chips.map((chip, chipIndex) => (
          <button
            type='button'
            key={chip.key}
            className={clsx(
              'relative inline-flex items-center px-3 py-2 text-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500',
              chipIndex > 0 && '-ml-px',
              chipIndex === 0 && 'rounded-l-md',
              chipIndex === chips.length - 1 && 'rounded-r-md',
              chip.selected ? 'bg-blue-200' : 'bg-white hover:bg-blue-100',
              disabled
                ? 'cursor-not-allowed text-gray-400 opacity-[70%]'
                : 'cursor-pointer'
            )}
            onClick={chip.onClick}
            disabled={disabled}
          >
            {chip.label}
          </button>
        ))}
      </span>
      {otherSelected && (
        <div className='w-24'>
          <NumberInput
            value={numericValue}
            onChange={emitChange}
            min={min}
            max={max}
            suffix={suffix}
            debounce={debounce}
            disabled={disabled}
            invalid={invalid}
            placeholder={placeholder}
            autoFocus={otherAutoFocus}
          />
        </div>
      )}
    </div>
  );
}
